import React, { useState, useEffect } from 'react';
import { X, Building, Bed, User } from 'lucide-react';

const HotelPlanModal = ({ isOpen, onClose, selectedRooms, hotelData, passengerDetails, onSave, existingRoomAssignments }) => {
  const [roomAssignments, setRoomAssignments] = useState({});

  useEffect(() => {
    if (isOpen) {
      // Check if we have existing room assignments to prefill
      if (existingRoomAssignments && typeof existingRoomAssignments === 'object' && Object.keys(existingRoomAssignments).length > 0) {
        setRoomAssignments(existingRoomAssignments);
      } else {
        // Initialize room assignments structure
        const initialAssignments = {};
        Object.entries(selectedRooms).forEach(([hotelId, rooms]) => {
          initialAssignments[hotelId] = {};
          Object.entries(rooms).forEach(([roomType, roomsArray]) => {
            initialAssignments[hotelId][roomType] = [];
            const count = Array.isArray(roomsArray) ? roomsArray.length : 0;
            for (let i = 0; i < count; i++) {
              initialAssignments[hotelId][roomType].push({
                roomNumber: i + 1,
                passengers: [],
                observations: ''
              });
            }
          });
        });
        setRoomAssignments(initialAssignments);
      }
    }
  }, [isOpen, selectedRooms, existingRoomAssignments]);

  if (!isOpen) return null;

  const getRoomTypeName = (type) => {
    const types = {
      roomsimple: 'Simple',
      roomdoble: 'Doble',
      roomtriple: 'Triple',
      roomcuadruple: 'Cuádruple'
    };
    return types[type] || type;
  };

  const getRoomCapacity = (roomType) => {
    const capacities = {
      roomsimple: 1,
      roomdoble: 2,
      roomtriple: 3,
      roomcuadruple: 4
    };
    return capacities[roomType] || 0;
  };

  const addPassengerToRoom = (hotelId, roomType, roomIndex, passengerName) => {
    const maxCapacity = getRoomCapacity(roomType);
    const currentPassengers = roomAssignments[hotelId]?.[roomType]?.[roomIndex]?.passengers || [];
    
    if (currentPassengers.length >= maxCapacity) {
      alert(`Esta habitación ya está llena (capacidad: ${maxCapacity})`);
      return;
    }

    if (currentPassengers.includes(passengerName)) {
      alert('Este pasajero ya está en esta habitación');
      return;
    }

    setRoomAssignments(prev => {
      const updated = { ...prev };
      if (!updated[hotelId]) updated[hotelId] = {};
      if (!updated[hotelId][roomType]) updated[hotelId][roomType] = [];
      if (!updated[hotelId][roomType][roomIndex]) {
        updated[hotelId][roomType][roomIndex] = { roomNumber: roomIndex + 1, passengers: [], observations: '' };
      }
      
      updated[hotelId][roomType][roomIndex] = {
        ...updated[hotelId][roomType][roomIndex],
        passengers: [...updated[hotelId][roomType][roomIndex].passengers, passengerName]
      };
      
      return updated;
    });
  };

  const removePassengerFromRoom = (hotelId, roomType, roomIndex, passengerName) => {
    setRoomAssignments(prev => {
      const updated = { ...prev };
      if (updated[hotelId]?.[roomType]?.[roomIndex]) {
        updated[hotelId][roomType][roomIndex] = {
          ...updated[hotelId][roomType][roomIndex],
          passengers: updated[hotelId][roomType][roomIndex].passengers.filter(p => p !== passengerName)
        };
      }
      return updated;
    });
  };

  const updateRoomObservations = (hotelId, roomType, roomIndex, observations) => {
    setRoomAssignments(prev => {
      const updated = { ...prev };
      if (updated[hotelId]?.[roomType]?.[roomIndex]) {
        updated[hotelId][roomType][roomIndex] = {
          ...updated[hotelId][roomType][roomIndex],
          observations
        };
      }
      return updated;
    });
  };

  const isPassengerAssigned = (passengerName) => {
    for (const hotelId in roomAssignments) {
      for (const roomType in roomAssignments[hotelId]) {
        const roomsForType = roomAssignments[hotelId]?.[roomType];
        if (!Array.isArray(roomsForType)) continue;

        for (const room of roomsForType) {
          if (!room || !Array.isArray(room.passengers)) continue;
          if (room.passengers.includes(passengerName)) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const handleSave = () => {
    onSave(roomAssignments);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto relative shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Plan de Hoteles - Asignación de Habitaciones
        </h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            Asigne los pasajeros a cada habitación. Seleccione un pasajero y luego haga clic en "Agregar" en la habitación deseada.
          </p>
        </div>

        {Object.entries(selectedRooms).map(([hotelId, rooms]) => {
          if (Object.keys(rooms).length === 0) return null;

          const hotel = hotelData.find(h => Object.keys(h)[0] === hotelId)?.[hotelId];
          const hotelName = hotel?.name || hotelId;

          return (
            <div key={hotelId} className="mb-6 border rounded-lg p-4 bg-gray-50">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Building className="w-5 h-5 mr-2 text-blue-500" />
                {hotelName}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(rooms).map(([roomType, roomsArray]) => {
                  const capacity = getRoomCapacity(roomType);
                  const roomTypeName = getRoomTypeName(roomType);
                  const count = Array.isArray(roomsArray) ? roomsArray.length : 0;

                  return Array.from({ length: count }, (_, roomIndex) => {
                    const assignedPassengers = roomAssignments[hotelId]?.[roomType]?.[roomIndex]?.passengers || [];
                    const roomObservations = roomAssignments[hotelId]?.[roomType]?.[roomIndex]?.observations || '';

                    return (
                      <div key={`${roomType}-${roomIndex}`} className="bg-white border border-gray-300 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <Bed className="w-4 h-4 text-green-500" />
                            <span className="font-medium text-sm">
                              {roomTypeName} #{roomIndex + 1}
                            </span>
                          </div>
                          <span className="text-xs text-gray-600">
                            {assignedPassengers.length}/{capacity}
                          </span>
                        </div>

                        {/* Assigned Passengers */}
                        <div className="mb-3 min-h-[60px]">
                          {assignedPassengers.length > 0 ? (
                            <div className="space-y-1">
                              {assignedPassengers.map((passenger, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-blue-50 p-2 rounded text-xs">
                                  <span className="flex items-center">
                                    <User className="w-3 h-3 mr-1" />
                                    {passenger}
                                  </span>
                                  <button
                                    onClick={() => removePassengerFromRoom(hotelId, roomType, roomIndex, passenger)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">Sin asignar</p>
                          )}
                        </div>

                        {/* Add Passenger Dropdown */}
                        {assignedPassengers.length < capacity && (
                          <select
                            className="w-full text-xs p-2 border border-gray-300 rounded mb-3"
                            onChange={(e) => {
                              if (e.target.value) {
                                addPassengerToRoom(hotelId, roomType, roomIndex, e.target.value);
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">Agregar pasajero...</option>
                            {passengerDetails
                              .filter(p => p.name && p.name.trim() !== '')
                              .map((passenger, idx) => (
                                <option
                                  key={idx}
                                  value={passenger.name}
                                  disabled={assignedPassengers.includes(passenger.name)}
                                >
                                  {passenger.name}
                                  {isPassengerAssigned(passenger.name) && !assignedPassengers.includes(passenger.name) ? ' (asignado)' : ''}
                                </option>
                              ))}
                          </select>
                        )}

                        {/* Observations */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Observaciones
                          </label>
                          <textarea
                            value={roomObservations}
                            onChange={(e) => updateRoomObservations(hotelId, roomType, roomIndex, e.target.value)}
                            placeholder="Agregar observaciones para esta habitación..."
                            className="w-full text-xs p-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={2}
                          />
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          );
        })}

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
          >
            Guardar Asignaciones
          </button>
        </div>
      </div>
    </div>
  );
};

export default HotelPlanModal;
